/**
 * Rhyme scheme pattern types and utilities
 *
 * Part of Scope 21: Per-Section-Type Rhyme Scheme Configuration
 */

import type { SectionType } from './song'

// Rhyme scheme pattern types
export type RhymeSchemePattern = 'AABB' | 'ABAB' | 'ABBA' | 'AAAA' | 'FREE' | 'CUSTOM'

export interface SectionRhymeScheme {
  pattern: RhymeSchemePattern
  customPattern?: string // Only used when pattern === 'CUSTOM'
}

// Map section type to its scheme configuration
export type SectionRhymeSchemes = Record<string, SectionRhymeScheme>

// Default schemes per section type
export const DEFAULT_SECTION_SCHEMES: SectionRhymeSchemes = {
  verse: { pattern: 'ABAB' },
  chorus: { pattern: 'AABB' },
  bridge: { pattern: 'FREE' },
  pre_chorus: { pattern: 'ABAB' },
  intro: { pattern: 'FREE' },
  outro: { pattern: 'FREE' },
  hook: { pattern: 'AABB' },
  custom: { pattern: 'FREE' },
}

// Help text for each pattern
export const PATTERN_HELP_TEXT: Record<RhymeSchemePattern, string> = {
  AABB: 'Couplets - lines rhyme in pairs (line 1-2, line 3-4)',
  ABAB: 'Alternating - every other line rhymes (line 1-3, line 2-4)',
  ABBA: 'Enclosed - first and last lines rhyme, middle lines rhyme',
  AAAA: 'Monorhyme - every line rhymes with every other',
  FREE: 'Free verse - no expected rhyme pattern',
  CUSTOM: 'Custom pattern - enter your own sequence (e.g., ABABCC)',
}

// Pattern options for dropdown
export const PATTERN_OPTIONS: Array<{ value: RhymeSchemePattern; label: string }> = [
  { value: 'AABB', label: 'AABB - Couplets' },
  { value: 'ABAB', label: 'ABAB - Alternating' },
  { value: 'ABBA', label: 'ABBA - Enclosed' },
  { value: 'AAAA', label: 'AAAA - Monorhyme' },
  { value: 'FREE', label: 'FREE - Free Verse' },
  { value: 'CUSTOM', label: 'CUSTOM - Custom Pattern' },
]

// Validation for custom pattern input
export function validateCustomPattern(pattern: string): boolean {
  // Must be uppercase letters only, at least 2 characters
  return /^[A-Z]{2,}$/.test(pattern)
}

// Get display label for a section type
export function getSectionTypeLabel(sectionType: SectionType): string {
  const labels: Record<SectionType, string> = {
    verse: 'Verse',
    chorus: 'Chorus',
    bridge: 'Bridge',
    pre_chorus: 'Pre-Chorus',
    intro: 'Intro',
    outro: 'Outro',
    hook: 'Hook',
    custom: 'Custom',
  }
  return labels[sectionType] || sectionType
}
